import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

// @ts-ignore
import { ReactComponent as AddIcon } from '../../../assets/images/add-circle-white.svg';
// @ts-ignore
import { ReactComponent as SettingsIcon } from '../../../assets/images/setting2-icon.svg';
// @ts-ignore
import { ReactComponent as EmptyPlanogramIcon } from '../../../assets/images/stock-levels/products-components-containers-empty.svg';
// @ts-ignore
import { ReactComponent as InitializationErrorRowIcon } from '../../../assets/images/planograms/initialisation-error-row.svg';
// @ts-ignore
import { ReactComponent as InitializationSuccessRowIcon } from '../../../assets/images/planograms/initialisation-success-row.svg';

import EmptyBlock from '../../../component/emptyblock/EmptyBlock';
import PlanogramCell from './PlanogramCell';
import PlanogramHeader from './PlanogramHeader';
import PlanogramSelectionMenu from './PlanogramSelectionMenu';
import PlanogramHeaderActions from './PlanogramHeaderActions';
import { PlanogramEditMode } from '../enums/PlanogramEditMode';
import { Checkbox } from 'primereact/checkbox';

import { useDragSelect, gridRangeResolvers } from './hooks/useDragSelect';
import { useCellClipboard } from './hooks/usePlanogramClipboard';
import { useCellArrowNavigation } from './hooks/useCellArrowNavigation';
import { useGridEnterNavigation } from './hooks/useGridEnterNavigation';
import { CellKey, cellKey, parseCellKey } from './domain/types';
import { nextVisualCellKey } from './domain/PlanogramNavigation';
import { PlanogramSelection } from './domain/PlanogramSelection';

// @ts-ignore
import './PlanogramVisual.scss';

type PlanogramEditModeValue = 'add_product' | 'initialization';

type ProductOption = {
    id?: string | number;
    value?: string | number;
    externalId?: string | number;
    systemId?: string | number;
    label?: string;
    name?: string;
    ean?: string | number;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    [key: string]: unknown;
};

type PlanogramCellType = {
    id?: string;
    type?: string;
    product?: {
        id?: string | number | null;
        name?: string;
        label?: string;
        price?: unknown;
        minQuantity?: unknown;
        maxQuantity?: unknown;
        [key: string]: unknown;
    } | null;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    merged?: boolean;
    mergeData?: {
        isMerged?: boolean;
        colspan?: number;
        [key: string]: unknown;
    } | null;
    mdbId?: string | number | null;
    evaDtsId?: string | number | null;
    [key: string]: unknown;
};

type PlanogramData = {
    id?: string;
    name?: string;
    rows: number;
    columns: number;
    cells: PlanogramCellType[][];
    systemId?: string | number;
    externalSystemId?: string | number;
    [key: string]: unknown;
};

type ActiveInitializationCell = {
    rowIndex?: number;
    colIndex?: number;
    [key: string]: unknown;
} | null;

type AssignIdForm = {
    rowIndex?: number;
    colIndex?: number;
    mdbId?: string;
    evaDtsId?: string;
    [key: string]: unknown;
};

type ProductSearchEvent = {
    query: string;
};

interface PlanogramVisualProps {
    planogramData?: PlanogramData | null;
    isSelectionMode: boolean;
    selectedCells?: Set<string>;

    onRenameClick?: () => void;
    onSelectModeToggle?: () => void;
    onPlanogramSave?: () => void;
    onClearSelectedCells?: () => void;
    onChangePriceForSelected?: () => void;
    onMergeSelected?: () => void;
    onCancelSelection?: () => void;
    onPlanogramCancel?: () => void;
    allSelectedCellsHaveProducts?: boolean;

    onSchemeSettingsClick?: () => void;
    onAddColumn?: () => void;
    onAddRow?: () => void;
    onContextMenu?: (event: React.MouseEvent) => void;

    onAddProduct?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellType,
        productOption: ProductOption,
    ) => void;

    onUpdateCellField?: (
        rowIndex: number,
        colIndex: number,
        field: string,
        value: string,
    ) => void;

    onEditProduct?: (rowIndex: number, colIndex: number) => void;
    onMergeRight?: (rowIndex: number, colIndex: number) => void;
    onMergeLeft?: (rowIndex: number, colIndex: number) => void;
    onMergeMultiple?: (rowIndex: number, colIndex: number) => void;
    onUnmerge?: (rowIndex: number, colIndex: number) => void;

    onAssignInitialization?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellType,
    ) => void;

    onEditAssign?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellType,
    ) => void;

    onTestCell?: (
        rowIndex: number,
        colIndex: number,
        cell: PlanogramCellType,
    ) => void;

    onClearCell?: (rowIndex: number, colIndex: number) => void;
    /** GH #555: see useCellClipboard's identical option — called instead of onClearCell-per-cell when Delete/Backspace is pressed with 2+ cells selected, so the parent can confirm first. */
    onClearSelectionRequest?: () => void;
    onCellSelection?: (rowIndex: number, colIndex: number) => void;
    onRowFullyMerged: (rowIndex: number) => boolean;

    isLoading?: boolean;
    onApplyToDevice?: () => void;
    showHeader?: boolean;
    mode?: PlanogramEditModeValue;
    onlyMerge?: boolean;
    manualInitializationActive?: boolean;
    supportEveDts?: boolean;

    assignIdForm?: AssignIdForm;
    assignIdErrors?: Record<string, unknown>;
    activeInitializationCell?: ActiveInitializationCell;
    onSaveAssignId?: () => void;
    onAssignIdFormChange?: (form: AssignIdForm) => void;
    onCancelAssignId?: () => void;
    onAssignProduct?: () => void;

    /**
     * Fired when a passive/view-only ADD_PRODUCT preview badge is
     * clicked (see PlanogramCell's renderAssignedSlotBadge) — lets
     * PlanogramDevice flip addProductEditActive on and show the full
     * NAME/PRICE/MIN/MAX editor cards, mirroring how clicking an
     * INITIALIZATION-preview badge re-enters manual MDB assignment via
     * onAssignInitialization.
     */
    onEnterAddProductEdit?: () => void;

    /**
     * Direct write-through handlers for the always-visible per-slot
     * MDB/EVA-DTS inputs (see PlanogramCell's renderInitializationSlotCard).
     * Same handlers PlanogramDevice already passes to PlanogramTabular
     * (handleChangeCellMdb/handleChangeCellEva) — they do duplicate
     * validation and the error toast there, so Visual reuses that logic
     * instead of duplicating it.
     */
    onChangeCellMdb?: (rowIndex: number, colIndex: number, value: string) => void;
    onChangeCellEva?: (rowIndex: number, colIndex: number, value: string) => void;
    invalidMdbCells?: string[];
    invalidEvaCells?: string[];

    isReadOnlyMode?: boolean;
    showPlanogramCells?: boolean;
    onShowPlanogramCells?: () => void;

    productSuggestions?: ProductOption[];
    productsLoading?: boolean;
    onSearchProducts?: (event: ProductSearchEvent) => void;
    onClearProductSearch?: (
        rowIndex: number,
        colIndex: number,
        text: string,
    ) => void;
    onDuplicateCell?: (
        sourceRowIndex: number,
        sourceColIndex: number,
        targetRowIndex: number,
        targetColIndex: number,
    ) => void;
    /**
     * Writes a previously-captured snapshot back into (rowIndex, colIndex)
     * — see useCellClipboard's identical option for the full GH #515
     * rationale. Threaded straight through to useCellClipboard below.
     */
    onRestoreCell?: (rowIndex: number, colIndex: number, snapshot: unknown) => void;
    /** Returns a deep-cloneable snapshot of the cell currently at (rowIndex, colIndex), read just before it's overwritten during mouse copy-mode. Paired with onRestoreCell above. */
    getCellSnapshot?: (rowIndex: number, colIndex: number) => unknown;
    onBulkCellSelection?: (cellKeys: string[], shouldSelect: boolean) => void;
    /**
     * Fired when a drag spanning 2+ cells completes while NOT already in
     * selection mode (and not in copy mode). The grid itself has no
     * business deciding what "entering selection mode" means at the
     * PlanogramDetails level — it just reports "the person dragged across
     * this many cells starting from a cold state" and hands back the full
     * resolved range; the parent decides to flip isSelectionMode on and
     * apply the selection. Single clicks (a 1-cell "drag") are NOT reported
     * here — those still require the explicit "Select" button, per design.
     */
    onSelectionDragCommit?: (cellKeys: string[]) => void;
    /** GH #555: see usePlanogramSelection.enterSelectionModeWith — enters selection mode with an initial set of keys already selected, used by Shift+Arrow's cold-start case. */
    onEnterSelectionModeWith?: (cellKeys: string[]) => void;
    /** GH #555: see usePlanogramSelection.replace — replaces the selection's contents outright, used by Shift+Arrow to recompute the anchor->focus rectangle on every subsequent press. */
    onReplaceSelection?: (cellKeys: string[]) => void;
    onEmptyProductAction?: (query: string) => void;

    /**
     * When true, this is the "blank layout" edit state: the planogram
     * hasn't been initialized yet and the person just pressed "Edit" to
     * define rows/columns/merges — before any MDB/EVA-DTS assignment or
     * product data exists. Threaded straight through to each
     * PlanogramCell so it can hide its own per-cell "more options"
     * trigger and any product-entry fields, the same way
     * PlanogramTabular's `actionsBody` skips its menu for this state.
     * See PlanogramDevice's `isUninitializedSlotLayoutEdit` for how this
     * is computed.
     *
     * NOTE: PlanogramCell.tsx must accept and honor this prop itself
     * (hiding its own toolbar/menu markup when true) for this to have
     * any visible effect — this component only forwards it.
     */
    hideCellMenu?: boolean;

    /**
     * ADD_PRODUCT "preview": the planogram already has products
     * assigned and the person hasn't pressed "Edit" yet (see
     * PlanogramDevice's `isAddProductPreview`). While true, the grid
     * shows plain numbered badges instead of editable cards (forwarded
     * to each PlanogramCell), and the structural controls that would
     * otherwise let someone reshape an already-live layout — the
     * settings gear, "add column", and "add row" buttons — are disabled
     * alongside genuine `isReadOnlyMode`, via the combined
     * `isStructureLocked` flag below.
     */
    isAddProductPreview?: boolean;
}

const PlanogramVisual = ({
    planogramData,
    isSelectionMode,
    selectedCells,
    onRenameClick,
    onSelectModeToggle,
    onPlanogramSave,
    onClearSelectedCells,
    onChangePriceForSelected,
    onMergeSelected,
    onCancelSelection,
    onPlanogramCancel,
    allSelectedCellsHaveProducts,
    onSchemeSettingsClick,
    onAddColumn,
    onAddRow,
    onContextMenu,
    onAddProduct,
    onUpdateCellField,
    onEditProduct,
    onMergeRight,
    onMergeLeft,
    onMergeMultiple,
    onUnmerge,
    onAssignInitialization,
    onEditAssign,
    onTestCell,
    onClearCell,
    onClearSelectionRequest,
    onCellSelection,
    onRowFullyMerged,
    isLoading,
    onApplyToDevice,
    showHeader = true,
    mode = PlanogramEditMode.ADD_PRODUCT as PlanogramEditModeValue,
    onlyMerge = false,
    manualInitializationActive = false,
    supportEveDts = false,
    assignIdForm,
    assignIdErrors,
    activeInitializationCell,
    onSaveAssignId,
    onAssignIdFormChange,
    onCancelAssignId,
    onAssignProduct,
    onEnterAddProductEdit,
    onChangeCellMdb,
    onChangeCellEva,
    invalidMdbCells = [],
    invalidEvaCells = [],
    isReadOnlyMode = false,
    showPlanogramCells = true,
    onShowPlanogramCells,
    productSuggestions = [],
    productsLoading = false,
    onSearchProducts,
    onClearProductSearch,
    onDuplicateCell,
    onRestoreCell,
    getCellSnapshot,
    onBulkCellSelection,
    onSelectionDragCommit,
    onEnterSelectionModeWith,
    onReplaceSelection,
    onEmptyProductAction,
    hideCellMenu = false,
    isAddProductPreview = false,
}: PlanogramVisualProps) => {
    const { t } = useTranslation();

    // Structural edits (grid size, merges) stay locked for real read-only
    // states, ADD_PRODUCT preview, and active MDB/EVA-DTS assignment.
    // Exception: not-initialized slot-layout edit mode. In that state the
    // person is defining rows/columns/merges before initialization, so the
    // settings gear and row/column add buttons must stay visible.
    const isUninitializedSlotLayoutEdit =
        mode === PlanogramEditMode.INITIALIZATION &&
        hideCellMenu &&
        !manualInitializationActive;

    const isStructureLocked =
        isReadOnlyMode ||
        isAddProductPreview ||
        manualInitializationActive ||
        (
            mode === PlanogramEditMode.INITIALIZATION &&
            !isUninitializedSlotLayoutEdit
        );

    const showStructureControls =
        !isStructureLocked &&
        Boolean(onSchemeSettingsClick) &&
        Boolean(onAddColumn) &&
        Boolean(onAddRow);

    // -------------------------------------------------------------------
    // Manual initialization validity: mirrors PlanogramTabular's
    // buildDuplicateKeySet/isMdbInvalidAt/isEvaInvalidAt so the Visual
    // grid can show the same green (valid/assigned) vs red (duplicate)
    // coloring PlanogramCell applies per-slot during manual MDB/EVA-DTS
    // assignment.
    // -------------------------------------------------------------------
    const buildDuplicateKeySet = (fieldName: 'mdbId' | 'evaDtsId'): Set<string> => {
        const valueToKeys = new Map<string, string[]>();
        const duplicateKeys = new Set<string>();
        if (!planogramData?.cells) return duplicateKeys;

        for (let r = 0; r < (planogramData.rows || 0); r++) {
            const row = planogramData.cells[r];
            if (!row) continue;
            for (let c = 0; c < (planogramData.columns || 0); c++) {
                const cell = row[c];
                if (!cell || (cell.merged && cell.mergeData?.isMerged)) continue;
                const raw = (cell as any)?.[fieldName];
                const value = typeof raw === 'string' ? raw.trim() : (raw != null ? String(raw).trim() : '');
                if (!value) continue;
                const key = `${r}-${c}`;
                if (!valueToKeys.has(value)) {
                    valueToKeys.set(value, [key]);
                } else {
                    valueToKeys.get(value)!.push(key);
                }
            }
        }
        for (const [, keys] of valueToKeys) {
            if (keys.length > 1) keys.forEach(k => duplicateKeys.add(k));
        }
        return duplicateKeys;
    };

    const getCellKey = (rowIndex: number, colIndex: number): string => `${rowIndex}-${colIndex}`;

    const hasRequiredMdb = (cell?: PlanogramCellType | null): boolean =>
        cell?.mdbId !== null &&
        cell?.mdbId !== undefined &&
        String(cell.mdbId).trim() !== '';

    const externalInvalidMdbKeys = new Set(invalidMdbCells);
    const externalInvalidEvaKeys = new Set(invalidEvaCells);

    const duplicateMdbKeys = buildDuplicateKeySet('mdbId');
    const duplicateEvaKeys = supportEveDts ? buildDuplicateKeySet('evaDtsId') : new Set<string>();

    const hasAnyAssignedMdb = Boolean(
        planogramData?.cells?.some(row =>
            row?.some(cell =>
                cell &&
                !(cell.merged && cell.mergeData?.isMerged) &&
                hasRequiredMdb(cell),
            ),
        ),
    );

    // Once assignment has started, an empty visible MDB slot should become red
    // in Visual too, matching Tabular and the Figma error state (e.g. 14/15 assigned).
    // A completely untouched grid stays neutral instead of becoming all red.
    const shouldMarkMissingMdb =
        mode === PlanogramEditMode.INITIALIZATION &&
        manualInitializationActive &&
        hasAnyAssignedMdb;

    const isMdbInvalidAt = (rowIndex: number, colIndex: number): boolean => {
        const key = getCellKey(rowIndex, colIndex);
        const cell = planogramData?.cells?.[rowIndex]?.[colIndex];

        return (
            duplicateMdbKeys.has(key) ||
            externalInvalidMdbKeys.has(key) ||
            (shouldMarkMissingMdb && !hasRequiredMdb(cell))
        );
    };

    const isEvaInvalidAt = (rowIndex: number, colIndex: number): boolean => {
        if (!supportEveDts) return false;

        const key = getCellKey(rowIndex, colIndex);

        return (
            duplicateEvaKeys.has(key) ||
            externalInvalidEvaKeys.has(key)
        );
    };

    const isCellIdInvalid = (rowIndex: number, colIndex: number): boolean =>
        isMdbInvalidAt(rowIndex, colIndex) || isEvaInvalidAt(rowIndex, colIndex);

    const isVisibleInitializationCell = (cell?: PlanogramCellType | null): boolean =>
        Boolean(cell && !(cell.merged && cell.mergeData?.isMerged));

    const isRowFullyAssigned = (rowIndex: number): boolean => {
        if (!planogramData?.cells?.[rowIndex]) return false;

        const visibleCells: PlanogramCellType[] = [];
        for (let colIndex = 0; colIndex < (planogramData.columns || 0); colIndex++) {
            const cell = planogramData.cells[rowIndex]?.[colIndex];
            if (isVisibleInitializationCell(cell)) {
                visibleCells.push(cell as PlanogramCellType);
            }
        }

        return (
            visibleCells.length > 0 &&
            visibleCells.every(cell => hasRequiredMdb(cell))
        );
    };


    const isRowInvalid = (rowIndex: number): boolean => {
        if (!planogramData?.cells?.[rowIndex]) return false;

        for (let colIndex = 0; colIndex < (planogramData.columns || 0); colIndex++) {
            const cell = planogramData.cells[rowIndex]?.[colIndex];
            if (!isVisibleInitializationCell(cell)) continue;
            if (isCellIdInvalid(rowIndex, colIndex)) return true;
        }

        return false;
    };

    const showInitializationRowStatus =
        mode === PlanogramEditMode.INITIALIZATION &&
        manualInitializationActive &&
        !hideCellMenu;

    const [selectedRow, setSelectedRow] = useState<number>(-1);
    const [activeCellKey, setActiveCellKey] = useState<CellKey | null>(null);

    // -------------------------------------------------------------------
    // Refs to every rendered slot's MDB input, keyed by "row-col" — lets
    // pressing Enter inside one slot's input move focus straight to the
    // next slot's, mirroring PlanogramTabular's identical
    // mdbInputRefs/focusNextMdb pattern (see that file for the original).
    // Respects the single-row filter the same way Tabular's version does
    // when a specific "Row N" view is selected: within that row, Enter
    // just walks columns; from "All", it walks the full grid in
    // row-then-column order and can cross into the next row.
    // -------------------------------------------------------------------
    const mdbInputRefs = useRef(new Map<string, HTMLInputElement>());

    const setMdbInputRef = useCallback((rowIndex: number, colIndex: number, el: HTMLInputElement | null) => {
        const key = `${rowIndex}-${colIndex}`;
        if (el) {
            mdbInputRefs.current.set(key, el);
        } else {
            mdbInputRefs.current.delete(key);
        }
    }, []);

    const focusNextMdb = useCallback((fromRowIndex: number, fromColIndex: number) => {
        if (!planogramData) return;
        const totalRows = planogramData.rows || 0;
        const totalCols = planogramData.columns || 0;
        if (!totalRows || !totalCols) return;

        if (selectedRow === -1) {
            for (let r = fromRowIndex; r < totalRows; r++) {
                const startCol = r === fromRowIndex ? fromColIndex + 1 : 0;
                for (let c = startCol; c < totalCols; c++) {
                    const el = mdbInputRefs.current.get(`${r}-${c}`);
                    if (el) {
                        el.focus();
                        return;
                    }
                }
            }
            return;
        }

        for (let c = fromColIndex + 1; c < totalCols; c++) {
            const el = mdbInputRefs.current.get(`${selectedRow}-${c}`);
            if (el) {
                el.focus();
                return;
            }
        }
    }, [planogramData, selectedRow]);

    // Guards against the click event that fires right after a drag-release,
    // so finishing a drag never also "activates" the cell under the cursor.
    // (useDragSelect.consumeWasDragging() now owns the underlying flag —
    // this local guard just reads it at the right moment in onClick below.)

    // Ref to the scrollable grid container. Used both to query/cache each
    // cell's geometry when a drag starts, and as the coordinate origin for
    // positioning the drag-preview overlay.
    const gridRef = useRef<HTMLDivElement | null>(null);

    // *** FIX (GH #543): clicking a different cell didn't blur whatever
    // input was focused in the previously-active one. ***
    //
    // setActiveCellKey only ever updated the CSS-visible "which cell looks
    // active" state — nothing ever told the BROWSER to move real focus
    // away from wherever it already was. Fix: whenever activeCellKey
    // changes to a cell that does NOT already contain the currently-
    // focused element, blur that element.
    useEffect(() => {
        const activeElement = document.activeElement as HTMLElement | null;

        if (!activeCellKey) {
            if (activeElement && activeElement !== document.body) {
                activeElement.blur();
            }
            return;
        }

        const { rowIndex, colIndex } = parseCellKey(activeCellKey);
        const activeCellElement = gridRef.current?.querySelector<HTMLElement>(
            `[data-row="${rowIndex}"][data-col="${colIndex}"]`,
        );

        if (!activeCellElement) {
            if (activeElement && activeElement !== document.body) {
                activeElement.blur();
            }
            return;
        }

        if (activeElement && activeElement !== document.body && activeCellElement.contains(activeElement)) {
            return;
        }

        if (mode === PlanogramEditMode.INITIALIZATION && manualInitializationActive) {
            const firstInput = activeCellElement.querySelector<HTMLInputElement>(
                'input:not(:disabled), textarea:not(:disabled), button:not(:disabled), [tabindex]:not([tabindex="-1"])',
            );

            if (firstInput) {
                firstInput.focus();
                return;
            }
        }

        if (activeElement && activeElement !== document.body) {
            activeElement.blur();
        }
    }, [activeCellKey, manualInitializationActive, mode]);

    // *** Enter / Shift+Enter grid navigation (separate from Tab — see
    // useGridEnterNavigation's own header comment for the full rationale).
    // Kept enabled during manual initialization so MDB/EVA-DTS inputs can
    // be traversed with Enter / Shift+Enter in the same order as Tab /
    // Shift+Tab.
    useGridEnterNavigation({
        containerRef: gridRef,
        rowItemSelector: '[data-row][data-col]',
        getRowOrder: cells =>
            [...cells].sort((a, b) => {
                const rowA = Number(a.dataset.row);
                const rowB = Number(b.dataset.row);
                if (rowA !== rowB) return rowA - rowB;
                return Number(a.dataset.col) - Number(b.dataset.col);
            }),
        disabled: isReadOnlyMode || !showPlanogramCells,
    });

    const getTitle = (): ReactNode =>
        planogramData?.name || t('planogram.pageTitle');

    // -------------------------------------------------------------------
    // Clipboard: mouse copy-mode + keyboard Ctrl+C/X/V/Esc/Delete.
    // -------------------------------------------------------------------
    const [clipboardToast, setClipboardToast] = useState<{
        label: string;
        top: number;
        left: number;
    } | null>(null);
    const clipboardToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showClipboardToast = useCallback((rowIndex: number, colIndex: number, label: string) => {
        const cellElement = gridRef.current?.querySelector<HTMLElement>(
            `[data-row="${rowIndex}"][data-col="${colIndex}"]`,
        );
        if (!cellElement) return;

        const rect = cellElement.getBoundingClientRect();
        setClipboardToast({ label, top: rect.top, left: rect.left + rect.width / 2 });

        if (clipboardToastTimeoutRef.current) clearTimeout(clipboardToastTimeoutRef.current);
        clipboardToastTimeoutRef.current = setTimeout(() => setClipboardToast(null), 1200);
    }, []);

    useEffect(() => {
        return () => {
            if (clipboardToastTimeoutRef.current) clearTimeout(clipboardToastTimeoutRef.current);
        };
    }, []);

    const clipboard = useCellClipboard({
        rows: planogramData?.rows ?? 0,
        columns: planogramData?.columns ?? 0,
        activeCellKey,
        setActiveCellKey,
        isSelectionMode,
        selectedKeys: selectedCells,
        onCancelSelection,
        onDuplicateCell: (sr, sc, tr, tc) => onDuplicateCell?.(sr, sc, tr, tc),
        onClearCell: (r, c) => onClearCell?.(r, c),
        onClearSelectionRequest: () => onClearSelectionRequest?.(),
        getCellSnapshot: (r, c) => getCellSnapshot?.(r, c),
        onRestoreCell: (r, c, snapshot) => onRestoreCell?.(r, c, snapshot),
        enableKeyboardShortcuts: true,
        onToast: showClipboardToast,
        labels: {
            cut: t('planogram.clipboard.cut', 'Cut'),
            copied: t('planogram.clipboard.copied', 'Copied'),
            copiedCount: (count: number) =>
                t('planogram.clipboard.copiedCount', { count, defaultValue: `Copied ${count} cells` }),
            pasted: t('planogram.clipboard.pasted', 'Pasted'),
        },
    });

    // -------------------------------------------------------------------
    // GH #555: Arrow keyboard navigation and range selection. Kept enabled
    // during manual initialization so Arrow keys can move the active slot
    // while MDB/EVA-DTS inputs are being configured.
    // -------------------------------------------------------------------
    useCellArrowNavigation({
        activeCellKey,
        setActiveCellKey,
        getNextKey: (from, direction) => {
            if (!planogramData) return null;
            const nextKey = nextVisualCellKey(planogramData.cells, planogramData.rows, planogramData.columns, from.rowIndex, from.colIndex, direction);
            if (!nextKey) return null;

            if (selectedRow !== -1) {
                const { rowIndex: nextRowIndex } = parseCellKey(nextKey);
                if (nextRowIndex !== selectedRow) return null;
            }

            return nextKey;
        },
        getFirstKey: () => {
            if (!planogramData) return null;
            const rowIndex = selectedRow === -1 ? 0 : selectedRow;
            const row = planogramData.cells[rowIndex];
            if (!row) return null;

            for (let colIndex = 0; colIndex < planogramData.columns; colIndex++) {
                const cell = row[colIndex];
                if (cell && !(cell.merged && cell.mergeData?.isMerged)) {
                    return cellKey(rowIndex, colIndex);
                }
            }
            return null;
        },
        computeRange: (anchor, focus) =>
            planogramData ? PlanogramSelection.rectangleKeys(planogramData.cells, anchor, focus) : [],
        isSelectionMode,
        enterSelectionModeWith: keys => onEnterSelectionModeWith?.(keys),
        replaceSelection: keys => onReplaceSelection?.(keys),
        exitSelectionMode: () => onCancelSelection?.(),
        disabled: isReadOnlyMode || !showPlanogramCells || clipboard.isCopyModeActive,
    });

    const handleAddProductsClick = () => {
        if (!showPlanogramCells) {
            onShowPlanogramCells?.();
            return;
        }
        onAssignProduct?.();
    };

    // -------------------------------------------------------------------
    // Drag-to-select / drag-to-copy across the grid.
    // -------------------------------------------------------------------
    const dragSelect = useDragSelect<CellKey>({
        captureItemRects: () => {
            const rects = new Map<CellKey, DOMRect>();
            const grid = gridRef.current;
            if (grid) {
                grid.querySelectorAll<HTMLElement>('[data-row][data-col]').forEach(element => {
                    const key = cellKey(Number(element.dataset.row), Number(element.dataset.col));
                    rects.set(key, element.getBoundingClientRect());
                });
            }
            return rects;
        },
        resolveKeyAtPoint: gridRangeResolvers.resolveKeyAtPoint,
        resolveRange: gridRangeResolvers.resolveRange,
        computePreviewRect: (rects, startKey, endKey) =>
            gridRangeResolvers.computePreviewRect(rects, startKey, endKey, gridRef.current),
        onCommit: keys => {
            if (clipboard.isCopyModeActive) {
                if (keys.length === 1) {
                    const { rowIndex: targetRowIndex, colIndex: targetColIndex } = parseCellKey(keys[0]);
                    clipboard.copyToCell(targetRowIndex, targetColIndex);
                } else {
                    clipboard.copyToRange(keys);
                }
                return;
            }

            if (isSelectionMode) {
                if (keys.length === 1) {
                    const isCurrentlySelected = !!selectedCells?.has(keys[0]);
                    onBulkCellSelection?.(keys, !isCurrentlySelected);
                } else {
                    onBulkCellSelection?.(keys, true);
                }
                return;
            }

            if (keys.length >= 2) {
                onSelectionDragCommit?.(keys);
            }
        },
    });

    const wasSelectionOrCopyActiveRef = useRef(isSelectionMode || clipboard.isCopyModeActive);
    useEffect(() => {
        const isActiveNow = isSelectionMode || clipboard.isCopyModeActive;
        if (wasSelectionOrCopyActiveRef.current && !isActiveNow) {
            dragSelect.resetIfInactive(false);
        }
        wasSelectionOrCopyActiveRef.current = isActiveNow;
    }, [isSelectionMode, clipboard.isCopyModeActive, dragSelect]);

    if (!planogramData) {
        return null;
    }

    const visibleRows =
        selectedRow === -1
            ? planogramData.cells.map((row, rowIndex) => ({
                  row,
                  rowIndex,
              }))
            : [
                  {
                      row: planogramData.cells[selectedRow],
                      rowIndex: selectedRow,
                  },
              ];

    const gridTemplateColumns = showStructureControls
        ? `38px repeat(${planogramData.columns}, 140px) 38px`
        : `38px repeat(${planogramData.columns}, 140px)`;

    return (
        <div
            className="planogram-container"
            onClick={() => setActiveCellKey(null)}
            style={dragSelect.isDragging ? { userSelect: 'none' } : undefined}
        >
            {showHeader && (
                <>
                    <PlanogramHeader
                        title={getTitle()}
                        columns={planogramData.columns}
                        rows={planogramData.rows}
                        onRenameClick={onRenameClick}
                        rightContent={null}
                        systemId={planogramData.systemId}
                        externalSystemId={planogramData.externalSystemId}
                        isReadOnlyMode={isReadOnlyMode}
                    >
                        <PlanogramHeaderActions
                            isSelectionMode={isSelectionMode}
                            isLoading={isLoading}
                            onSelectModeToggle={onSelectModeToggle}
                            onPlanogramSave={onPlanogramSave}
                            onApplyToDevice={onApplyToDevice}
                            onAssignProduct={handleAddProductsClick}
                            onSchemeSettingsClick={showStructureControls ? onSchemeSettingsClick : undefined}
                            isReadOnlyMode={isReadOnlyMode}
                            isEmptyState={!showPlanogramCells}
                            onPlanogramCancel={onPlanogramCancel}
                            disableApplyToDevice
                        />
                    </PlanogramHeader>

                    <div className="separator" />
                </>
            )}

            {showPlanogramCells && (
                <PlanogramSelectionMenu
                    isSelectionMode={isSelectionMode}
                    selectedCells={selectedCells}
                    onAssignProduct={onAssignProduct}
                    onClearSelectedCells={onClearSelectedCells}
                    onChangePriceForSelected={onChangePriceForSelected}
                    onMergeSelected={onMergeSelected}
                    onCancelSelection={onCancelSelection}
                    allSelectedCellsHaveProducts={allSelectedCellsHaveProducts}
                />
            )}

            {!showPlanogramCells ? (
                <EmptyBlock
                    className="planogram-products-empty"
                    icon={<EmptyPlanogramIcon />}
                    title={t(
                        'planogram.emptyProducts.title',
                        'Planogram products required',
                    )}
                    description={t(
                        'planogram.emptyProducts.description',
                        'This planogram requires at least one sellable product. For standard vending machines, the planogram defines which products are available for sale and how they are assigned to specific slots, shelves, or selections within the machine. Each planogram position should be linked to a product that can be purchased by the customer.',
                    )}
                />
            ) : (
                <>
                    <div
                        className="visual-row-selection"
                        style={{ gridTemplateColumns }}
                    >
                        <div className="visual-row-buttons">
                            <Button
                                className={`visual-row-button ${
                                    selectedRow === -1 ? 'active' : ''
                                }`}
                                onClick={() => setSelectedRow(-1)}
                            >
                                {t('planogram.rows.all', 'All')}
                            </Button>

                            {Array.from(
                                { length: planogramData.rows || 0 },
                                (_, index) => (
                                    <Button
                                        key={index}
                                        className={`visual-row-button ${
                                            selectedRow === index
                                                ? 'active'
                                                : ''
                                        }`}
                                        onClick={() => setSelectedRow(index)}
                                    >
                                        {t('planogram.rows.row', {
                                            index: index + 1,
                                            defaultValue: `Row ${
                                                index + 1
                                            }`,
                                        })}
                                        {showInitializationRowStatus && (
                                            isRowInvalid(index) ? (
                                                <InitializationErrorRowIcon
                                                    className="row-button-status-icon row-button-error-icon"
                                                    aria-hidden="true"
                                                />
                                            ) : (
                                                isRowFullyAssigned(index) && (
                                                    <InitializationSuccessRowIcon
                                                        className="row-button-status-icon row-button-success-icon"
                                                        aria-hidden="true"
                                                    />
                                                )
                                            )
                                        )}
                                    </Button>
                                ),
                            )}
                        </div>

                        {showStructureControls && (
                            <Button
                                type="button"
                                className="visual-settings-button"
                                onClick={onSchemeSettingsClick}
                            >
                                <SettingsIcon />
                            </Button>
                        )}
                    </div>

                    <div className="planogram-grid" ref={gridRef}>
                        <div
                            className="column-headers"
                            style={{ gridTemplateColumns }}
                        >
                            <div className="grid-corner-cell" />

                            {Array.from({ length: planogramData.columns }, (_, index) => {
                                const columnKeys = Array.from(
                                    { length: planogramData.rows },
                                    (_, rowIndex) => cellKey(rowIndex, index),
                                );

                                const isColumnCopied = clipboard.areAllCopied(columnKeys);
                                const isColumnSelected = columnKeys.every(key => selectedCells?.has(key));

                                return (
                                    <div key={index} className="column-header">
                                        {clipboard.isCopyModeActive && (
                                            <Checkbox
                                                className="copy-mode-checkbox header-checkbox"
                                                checked={isColumnCopied}
                                                onClick={event => event.stopPropagation()}
                                                onChange={() => clipboard.copyToRange(columnKeys)}
                                            />
                                        )}

                                        {!clipboard.isCopyModeActive && isSelectionMode && (
                                            <Checkbox
                                                className="copy-mode-checkbox header-checkbox"
                                                checked={isColumnSelected}
                                                onClick={event => event.stopPropagation()}
                                                onChange={() => onBulkCellSelection?.(columnKeys, !isColumnSelected)}
                                            />
                                        )}

                                        {index + 1}
                                    </div>
                                );
                            })}

                            {/* <div className="column-header-spacer" /> */}

                            {showStructureControls && (
                                <div
                                    className="add-column-button"
                                    onClick={event => {
                                        event.stopPropagation();
                                        onAddColumn?.();
                                    }}
                                >
                                    <AddIcon />
                                </div>
                            )}
                        </div>

                        {visibleRows.map(({ row, rowIndex }) => {
                            const rowKeys = Array.from(
                                { length: planogramData.columns },
                                (_, colIndex) => cellKey(rowIndex, colIndex),
                            );

                            const isRowCopied = clipboard.areAllCopied(rowKeys);
                            const isRowSelected = rowKeys.every(key => selectedCells?.has(key));

                            return (
                                <div
                                    key={rowIndex}
                                    className="grid-row"
                                    style={{ gridTemplateColumns }}
                                >
                                    <div className="row-header">
                                        {clipboard.isCopyModeActive && (
                                            <Checkbox
                                                className="copy-mode-checkbox header-checkbox"
                                                checked={isRowCopied}
                                                onClick={event => event.stopPropagation()}
                                                onChange={() => clipboard.copyToRange(rowKeys)}
                                            />
                                        )}

                                        {!clipboard.isCopyModeActive && isSelectionMode && (
                                            <Checkbox
                                                className="copy-mode-checkbox header-checkbox"
                                                checked={isRowSelected}
                                                onClick={event => event.stopPropagation()}
                                                onChange={() => onBulkCellSelection?.(rowKeys, !isRowSelected)}
                                            />
                                        )}

                                        {rowIndex + 1}
                                    </div>

                                    {row.map((cell, colIndex) => {
                                        if (
                                            cell.merged &&
                                            cell.mergeData?.isMerged
                                        ) {
                                            return null;
                                        }

                                        const colspan =
                                            cell.mergeData?.colspan || 1;

                                        const isMergedCell =
                                            !!cell.merged &&
                                            !!cell.mergeData?.colspan &&
                                            cell.mergeData.colspan > 1;

                                        const isManualSelected =
                                            !!manualInitializationActive &&
                                            !!assignIdForm &&
                                            assignIdForm.rowIndex === rowIndex &&
                                            assignIdForm.colIndex === colIndex;

                                        const hasMdbId = !!cell?.mdbId;

                                        const isCellDataInvalid = isCellIdInvalid(rowIndex, colIndex);

                                        const hasCellProduct =
                                            cell?.type === 'product' &&
                                            !!cell.product &&
                                            !!cell.product.id;

                                        const thisCellKey = cellKey(rowIndex, colIndex);
                                        const isCellActive = activeCellKey === thisCellKey;
                                        const hasActiveCell = activeCellKey !== null;
                                        const isCellCopied = clipboard.isCopied(rowIndex, colIndex);

                                        const mergedCellStyle: CSSProperties =
                                            isMergedCell
                                                ? {
                                                      gridColumnStart:
                                                          colIndex + 2,
                                                      gridColumnEnd:
                                                          colIndex + 2 + colspan,
                                                  }
                                                : {};

                                        return (
                                            <div
                                                key={colIndex}
                                                data-row={rowIndex}
                                                data-col={colIndex}
                                                className={`grid-cell ${
                                                    isMergedCell ? 'merged-cell' : ''
                                                } ${
                                                    isManualSelected ? 'manual-selected' : ''
                                                } ${
                                                    isManualSelected && hasMdbId ? 'manual-selected-has-id' : ''
                                                } ${
                                                    isCellActive ? 'active-cell' : ''
                                                } ${
                                                    hasActiveCell && !isCellActive ? 'inactive-cell' : ''
                                                } ${
                                                    hasActiveCell && !isCellActive && hasCellProduct ? 'has-data-cell' : ''
                                                } ${
                                                    isReadOnlyMode ? 'read-only-cell' : ''
                                                } ${dragSelect.isDragging ? 'drag-in-progress' : ''
                                                } `}
                                                style={mergedCellStyle}
                                                onClick={event => {
                                                    event.stopPropagation();

                                                    if (dragSelect.consumeWasDragging()) {
                                                        return;
                                                    }

                                                    if (isSelectionMode) {
                                                        return;
                                                    }

                                                    setActiveCellKey(thisCellKey);
                                                }}
                                                onMouseDown={event => {
                                                    if (isReadOnlyMode) return;

                                                    const isColdStart = !isSelectionMode && !clipboard.isCopyModeActive;
                                                    if (isColdStart) {
                                                        const target = event.target as HTMLElement | null;
                                                        const clickedInteractiveChild = Boolean(
                                                            target?.closest('input, button, textarea, .cell-toolbar-btn, .cell-product-autocomplete, .selection-checkbox'),
                                                        );
                                                        if (clickedInteractiveChild) return;
                                                    }

                                                    event.preventDefault();
                                                    event.stopPropagation();

                                                    dragSelect.startDrag(thisCellKey);
                                                }}
                                                onContextMenu={onContextMenu}
                                            >
                                                {clipboard.isCopyModeActive && (
                                                    <Checkbox
                                                        className="copy-mode-checkbox"
                                                        checked={isCellCopied}
                                                        onMouseDown={event => event.stopPropagation()}
                                                        onClick={event => event.stopPropagation()}
                                                        onChange={() => clipboard.copyToCell(rowIndex, colIndex)}
                                                    />
                                                )}

                                                <PlanogramCell
                                                    cell={cell}
                                                    rowIndex={rowIndex}
                                                    colIndex={colIndex}
                                                    onAddProduct={onAddProduct}
                                                    onUpdateCellField={
                                                        onUpdateCellField
                                                    }
                                                    onEditProduct={onEditProduct}
                                                    onMergeRight={onMergeRight}
                                                    onMergeLeft={onMergeLeft}
                                                    onMergeMultiple={
                                                        onMergeMultiple
                                                    }
                                                    onUnmerge={onUnmerge}
                                                    onClearCell={onClearCell}
                                                    isSelectionMode={
                                                        isSelectionMode
                                                    }
                                                    isSelected={
                                                        !!selectedCells &&
                                                        selectedCells.has(
                                                            thisCellKey,
                                                        )
                                                    }
                                                    onSelectionToggle={
                                                        onCellSelection
                                                    }
                                                    totalColumns={
                                                        planogramData.columns
                                                    }
                                                    isRowFullyMerged={onRowFullyMerged(
                                                        rowIndex,
                                                    )}
                                                    mode={mode}
                                                    onAssignInitialization={
                                                        onAssignInitialization
                                                    }
                                                    onEnterAddProductEdit={onEnterAddProductEdit}
                                                    isReadOnlyMode={isReadOnlyMode}
                                                    onEditAssign={onEditAssign}
                                                    onTestCell={onTestCell}
                                                    onlyMerge={onlyMerge}
                                                    manualInitializationActive={
                                                        manualInitializationActive
                                                    }
                                                    activeInitializationCell={
                                                        activeInitializationCell
                                                    }
                                                    assignIdForm={assignIdForm}
                                                    onSaveAssignId={onSaveAssignId}
                                                    onAssignIdFormChange={
                                                        onAssignIdFormChange
                                                    }
                                                    onCancelAssignId={
                                                        onCancelAssignId
                                                    }
                                                    onChangeMdbId={onChangeCellMdb}
                                                    onChangeEvaDtsId={onChangeCellEva}
                                                    onEnterNextSlot={focusNextMdb}
                                                    registerMdbInputRef={setMdbInputRef}
                                                    supportEveDts={supportEveDts}
                                                    assignIdErrors={
                                                        assignIdErrors
                                                    }
                                                    productSuggestions={
                                                        productSuggestions
                                                    }
                                                    productsLoading={
                                                        productsLoading
                                                    }
                                                    onSearchProducts={
                                                        onSearchProducts
                                                    }
                                                    onClearProductSearch={
                                                        onClearProductSearch
                                                    }
                                                    onDuplicateCell={() => {
                                                        clipboard.startCopyMode(rowIndex, colIndex);
                                                    }}
                                                    isCopySourceCell={
                                                        clipboard.isCopySourceCell(rowIndex, colIndex)
                                                    }
                                                    isCopyModeActive={clipboard.isCopyModeActive}
                                                    copiedCellsCount={clipboard.copiedCount}
                                                    onCancelCopyMode={clipboard.cancelCopyMode}
                                                    onConfirmCopyMode={clipboard.confirmCopyMode}
                                                    onEmptyProductAction={onEmptyProductAction}
                                                    hideCellMenu={hideCellMenu}
                                                    isAddProductPreview={isAddProductPreview}
                                                    isInvalid={isCellDataInvalid}
                                                    onCellFocus={(focusedRowIndex, focusedColIndex) => {
                                                        setActiveCellKey(cellKey(focusedRowIndex, focusedColIndex));
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}

                                    {/* <div className="grid-row-spacer" /> */}
                                    {showStructureControls && (
                                        <div className="grid-row-action-spacer" />
                                    )}
                                </div>
                            );
                        })}

                        {showStructureControls && (
                            <div
                                className="add-row-button"
                                onClick={event => {
                                    event.stopPropagation();
                                    onAddRow?.();
                                }}
                            >
                                <AddIcon />
                            </div>
                        )}

                        {dragSelect.isDragging && dragSelect.previewRect && (
                            <div
                                className="drag-selection-overlay"
                                style={{
                                    position: 'absolute',
                                    top: dragSelect.previewRect.top,
                                    left: dragSelect.previewRect.left,
                                    width: dragSelect.previewRect.width,
                                    height: dragSelect.previewRect.height,
                                    pointerEvents: 'none',
                                }}
                            >
                                <div className="drag-selection-handle" />
                            </div>
                        )}
                    </div>
                </>
            )}

            {clipboardToast && (
                <div
                    className="cell-toolbar-js-tooltip clipboard-toast"
                    style={{
                        position: 'fixed',
                        top: clipboardToast.top,
                        left: clipboardToast.left,
                        transform: 'translate(-50%, -100%) translateY(-8px)',
                    }}
                >
                    {clipboardToast.label}
                </div>
            )}
        </div>
    );
};

export default PlanogramVisual;